<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">MARKET RENT WORKSHEET > TOP SUMMARY SECTION</h1>
    <span class="banner py-3 mb-4">
      <div>The three tabs contained in this worksheet will allow you to establish your Base Rent, Renewal Premiums, and Amenities. The total of these three will result in Market Rent or Gross Potential Rent. This top section will display the resulting calculations from the three tabs below.</div>
      <br/>
      <div>Expand any of the topics below for more information.</div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Top Row Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>The options at the top allow you to select how many years of historical data to display. It also provides an option to add YearOverYear Variance Dollar and YearOverYear Variance Percent columns to the calculation table.</div>
          <div><strong>Historical Definitions</strong> - If you select to see one or more years of historical information, it will be displayed on the following lines.</div>
          <ul>
            <li><strong>TOTAL GPR</strong> - This can be verified by running a Gross Potential Rent report, Market Rent column for that month and year.</li>
            <li><strong>Average GPR (Unit)</strong> - This can be verified by taking TOTAL GPR divided by the Unit count, which can be located by running a Rent Roll Summary and looking at the total unit count in the Property Occupancy section.</li>
            <li><strong>Average GPR (Sq Ft)</strong> - This can be verified by taking TOTAL GPR divided by the square footage, which can be located by running a Rent Roll Summary and looking at the total square footage in the Property Occupancy section.</li>
            <li>There is no historical information provided for Base Rent, Reno Premium, or Amenity Premium.</li>
          </ul>
          <br/>
          <div><strong>YearOverYear Column Definitions:</strong></div>
          <ul>
            <li><strong>YoY Variance $</strong> - Total for this 12 months - total for prior 12 months (as a dollar amount)</li>
            <li><strong>YoY Variance %</strong> - Total for this 12 months vs total for prior 12 months (as a percent)</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Calculation Table</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>This section displays resulting totals as you move through the Base Rent, Renovations, and Amenities Tabs. These three numbers are added up for the Total GPR.</div>
          <br/>
          <ul>
            <li><strong>Base Rent</strong> - Verify this total on the Base Rent tab. Add up each unit type's monthly numbers.</li>
            <li><strong>Reno Premiums</strong> - Verify this total from the Renovations tab.</li>
            <li class="no-bullet">
              <ul>
                <li>If Property Level is selected, this will be equal to the Projected Renovation Revenue.</li>
                <li>If Unit Type Level is selected, this will be equal to the Projected Renovation Revenue summed up for all the unit types.</li>
              </ul>
            </li>
            <li><strong>Amenity Premiums</strong> - Verify this total from the Amenities tab. This will be equal to summing up each Unit Type.</li>
            <li><strong>Total GPR</strong> - Base Rent + Reno Premiums + Amenity Premiums</li>
            <li class="no-bullet">
              <ul>
                <li>
                  <strong>Historicals</strong>: matches the totals of a Market Rent Schedule report: Property Total - Cumulative Rent column.
                </li>
              </ul>
            </li>
            <li><strong>Average GPR (Unit)</strong> - Total GPR / Number of Units (including down/admin/model) The number of units can be verified by the totals of a Rent Roll Summary report.</li>
            <li class="no-bullet">
              <ul>
                <li>
                  <strong>Historicals</strong>: Historical TOTAL GPR / Property Total Units
                </li>
              </ul>
            </li>
            <li><strong>Average GPR (Sq Ft)</strong> - total GPR / Total Square Footage (including down/admin/model). The square footage can be verified by the totals of a Rent Roll Summary report.</li>
            <li class="no-bullet">
              <ul>
                <li>
                  <strong>Historicals</strong>: Historical TOTAL GPR / Property Square Footage
                </li>
              </ul>
            </li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro Market Rent')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'MarketRentSummaryHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  .icon {
      order: 0;
  }
  .header {
      order: 1;
  }
  .help-container {
      border-bottom: 1px solid var(--v-resGrey-lighten1)
  }
  .help-content div,
  .help-content li {
      margin-top:10px !important;
      margin-bottom:10px !important;
  }
  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }
  .pointer {
      cursor: pointer !important;
  }
  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }
  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

.separator:not(:empty)::after {
  margin-left: .25em;
}
</style>
